import { Launch } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import { Link } from 'react-router-dom';
import { homeStyles } from '..';
import { ARMToday, CRMToday } from '../../../types';
import { captureError } from '../../../utils/capture-error';
import { isAgt } from '../../../utils/is-agent';
import { getTodayTotals } from '../../business/crm/today-utils';
import { getARMTodayTotals } from '../../business/crm/today-utils-arm';

const styles = {
  totals: {
    p: 1,
    letterSpacing: 0,
    fontSize: 36,
    fontWeight: 300,
  },
};

export function CRMTotals(props: { AgtNo: string }) {
  // Props
  const { AgtNo } = props;
  // Hooks
  const labelSize = 9;
  const fontWeight = 200;
  const fontFamily = 'Roboto';

  // Query - Sales CRM Today
  const salesPath = '/homepage/sales-crm';
  const salesQueryKey = [salesPath, { AgtNo }];
  const salesQuery = useQuery({
    queryKey: salesQueryKey,
    queryFn: async () => {
      const responseSales: {
        data: CRMToday;
      } = await API.post('DashboardAPI', salesPath, { body: { AgtNo } });

      if (responseSales.data) {
        return getTodayTotals({
          data: {
            ...responseSales.data,
            chargebacks: [],
            medicare: [],
            foresters_children_wl: [],
            moo_children_wl: [],
          },
          showCompleted: false,
        });
      }
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Team CRM Milestones
  const teamPath = '/crm/agentmilestone';
  const teamQueryKey = [teamPath, { AgtNo }];
  const teamQuery = useQuery({
    enabled: isAgt(AgtNo),
    queryKey: teamQueryKey,
    queryFn: async () => {
      const responseTeam: {
        data: ARMToday | null;
      } = await API.post('DashboardAPI', teamPath, {
        body: { agentno: AgtNo },
      });

      if (responseTeam.data) {
        return getARMTodayTotals({
          data: responseTeam.data,
          showCompleted: false,
        });
      }
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const crmLink = (
    <Tooltip
      title="Go to CRM"
      arrow
      placement="top"
      slotProps={{
        tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
        arrow: { sx: { color: '#17191B' } },
      }}
    >
      <Link
        to="/business/crm"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#fff', display: 'flex', alignItems: 'center' }}
      >
        <Launch fontSize="small" sx={{ transform: 'scale(.8)' }} />
      </Link>
    </Tooltip>
  );

  return (
    <Stack
      direction="column"
      spacing={2}
      width="100%"
      height="100%"
      alignItems="stretch"
      flex={1}
      sx={{ whiteSpace: 'nowrap' }}
    >
      <Box p={1} height="100%" minHeight={120} sx={{ ...homeStyles.box }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            letterSpacing: 1.1,
          }}
        >
          SALES CRM {crmLink}
        </Box>
        {salesQuery.isFetching ? (
          <Skeleton height={85} />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-evenly"
            spacing={1}
            sx={{ ...styles.totals }}
          >
            <Box flex={1}>
              <Box>{salesQuery.data?.birthdayCount || 0} </Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                Birthdays
              </Box>
            </Box>
            <Box flex={1}>
              <Box>{salesQuery.data?.reviewCount || 0} </Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                Policy Reviews
              </Box>
            </Box>
            <Box flex={1}>
              <Box>{salesQuery.data?.conversionCount || 0}</Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                Conversions
              </Box>
            </Box>
          </Stack>
        )}
      </Box>

      <Box p={1} height="100%" minHeight={120} sx={{ ...homeStyles.box }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          TEAM CRM {crmLink}
        </Box>
        {teamQuery.isFetching ? (
          <Skeleton height={85} />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-evenly"
            spacing={1}
            sx={{ ...styles.totals }}
          >
            <Box flex={1}>
              <Box>{teamQuery.data?.DOBCount || 0}</Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                Birthdays
              </Box>
            </Box>
            <Box flex={1}>
              <Box>{teamQuery.data?.FirstARCDepositCount || 0}</Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                1st ARC Deposit
              </Box>
            </Box>
            <Box flex={1}>
              <Box>{teamQuery.data?.FirstFastractedAgentCount || 0}</Box>
              <Box
                sx={{
                  fontSize: labelSize,
                  fontWeight: fontWeight,
                  fontFamily: fontFamily,
                }}
              >
                1st FasTract
              </Box>
            </Box>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
